// Gets a cookie by name on the client side
export const getCookie = (name: string) => {
  const nameString = name + '=';
  const value = document.cookie.split('; ').find((row) => row.startsWith(nameString));
  return value ? decodeURIComponent(value.split('=')[1]) : null;
};

// Sanitize string against XSS
function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove onclick= and similar
    .replace(/data:/gi, ''); // Remove data: protocol
}

// Sets a cookie value on the client side
export const setCookie = (name: string, value: string, days: number = 30) => {
  const sanitizedName = sanitizeInput(name);
  const sanitizedValue = sanitizeInput(value);
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${sanitizedName}=${encodeURIComponent(sanitizedValue)};expires=${expires.toUTCString()};path=/;SameSite=Lax;Secure`;
};

// Helper function to parse cookies from the request headers
const parseCookies = (request: Request): Record<string, string> => {
  const cookieHeader = request.headers.get('cookie') || '';
  const cookies: Record<string, string> = {};

  cookieHeader.split(';').forEach((cookie) => {
    const [name, ...rest] = cookie.trim().split('=');
    const value = rest.join('=');

    if (name && value) {
      cookies[name] = decodeURIComponent(value);
    }
  });

  return cookies;
};

// Gets a cookie value by name
export const getCookieOnServer = (request: Request, name: string): string | null => {
  const cookies = parseCookies(request);

  return cookies[name] || null;
};

// Sets a cookie value
export const setCookieOnServer = (headers: Headers, name: string, value: string, days: number = 30): void => {
  const sanitizedName = sanitizeInput(name);
  const sanitizedValue = sanitizeInput(value);
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  const cookie = `${sanitizedName}=${encodeURIComponent(sanitizedValue)}; Expires=${expires.toUTCString()}; Path=/; SameSite=Lax; Secure`;

  // Append the Set-Cookie header
  headers.append('Set-Cookie', cookie);
};

// Gets a promo or offer code from the URL or cookie
export const getPromotionCookie = (request: Request, headers: Headers, cookieName = 'offer'): string | null => {
  // Check for offer in the request URL
  const url = new URL(request.url);
  const param = url.searchParams.get(cookieName);

  if (param) {
    const code = param.toUpperCase();
    const existingOffer = getCookieOnServer(request, cookieName);

    // If the user already has an offer cookie set, check it against the offer code in the URL
    if (existingOffer && existingOffer.toUpperCase() === code) {
      return code;
    } else {
      // Set the cookie to the new value
      setCookieOnServer(headers, cookieName, code, 7);
      return code;
    }
  }

  // Check for offer cookie if not found in URL
  return getCookieOnServer(request, cookieName);
};

export const createMarketingCookies = (request: Request, headers: Headers): void => {
  createHasOffersCookies(request, headers);
  createUtmDataCookies(request, headers);
  createCohortCookie(request, headers);
};

// Creates HasOffers tracking cookies based on URL parameters
const createHasOffersCookies = (request: Request, headers: Headers): void => {
  const url = new URL(request.url);
  const utmSource = url.searchParams.get('utm_source') || '';
  const hasoffersTid = url.searchParams.get('afftid') || url.searchParams.get('tid') || '';
  const hasoffersAffid = url.searchParams.get('affid') || '';

  if (utmSource === 'hasoffers') {
    if (hasoffersTid) {
      setCookieOnServer(headers, 'hasoffers_tid', hasoffersTid, 90);
    }

    if (hasoffersAffid) {
      setCookieOnServer(headers, 'hasoffers_affid', hasoffersAffid, 90);
    }
  }
};

function createUtmDataCookies(request: Request, headers: Headers): void {
  const url = new URL(request.url);
  const utmSource = sanitizeInput(url.searchParams.get('utm_source') || '');
  const utmMedium = sanitizeInput(url.searchParams.get('utm_medium') || '');
  const utmCampaign = sanitizeInput(url.searchParams.get('utm_campaign') || '');
  const utmContent = sanitizeInput(url.searchParams.get('utm_content') || '');
  const utmTerm = sanitizeInput(url.searchParams.get('utm_term') || '');

  const cookieUpdateNeeded = utmSource || utmMedium || utmCampaign || utmContent || utmTerm;
  let utmDataArray = getCookieOnServer(request, 'utmData') ? JSON.parse(getCookieOnServer(request, 'utmData')!) : {};
  const utmDataInitialArray = getCookieOnServer(request, 'utmDataInitial') ? JSON.parse(getCookieOnServer(request, 'utmDataInitial')!) : {};
  const isInitialUtmDataCookieArrayEmpty = Object.keys(utmDataInitialArray).length === 0;

  if (cookieUpdateNeeded) {
    utmDataArray = {};

    if (utmSource) {
      utmDataArray.utm_source = utmSource;

      if (isInitialUtmDataCookieArrayEmpty) {
        utmDataInitialArray.utm_source = utmSource;
      }
    }

    if (utmMedium) {
      utmDataArray.utm_medium = utmMedium;

      if (isInitialUtmDataCookieArrayEmpty) {
        utmDataInitialArray.utm_medium = utmMedium;
      }
    }

    if (utmCampaign) {
      utmDataArray.utm_campaign = utmCampaign;

      if (isInitialUtmDataCookieArrayEmpty) {
        utmDataInitialArray.utm_campaign = utmCampaign;
      }
    }

    if (utmContent) {
      utmDataArray.utm_content = utmContent;

      if (isInitialUtmDataCookieArrayEmpty) {
        utmDataInitialArray.utm_content = utmContent;
      }
    }

    if (utmTerm) {
      utmDataArray.utm_term = utmTerm;

      if (isInitialUtmDataCookieArrayEmpty) {
        utmDataInitialArray.utm_term = utmTerm;
      }
    }

    setCookieOnServer(headers, 'utmData', JSON.stringify(utmDataArray), 60);
    setCookieOnServer(headers, 'utmDataInitial', JSON.stringify(utmDataInitialArray), 60);
  }
}

function createCohortCookie(request: Request, headers: Headers): void {
  const url = new URL(request.url);
  const cohort = url.searchParams.get('cohort') || '';

  if (cohort) {
    setCookieOnServer(headers, 'cohort', cohort, 60);
  }
}
